import type { ArrivalAnnotation } from '@/shared/api';

/**
 * Zeichnet die Markierungen als SVG über das Original — nie hinein. Der Pfeil bleibt damit
 * korrigierbar, das Bild wiederverwendbar, und dieselbe Zeichnung sitzt auf jedem Display
 * richtig, weil die Koordinaten relativ sind (0–1) und das SVG mitskaliert.
 */
export function AnnotationOverlay({ annotations }: { annotations: ArrivalAnnotation[] }) {
  if (annotations.length === 0) return null;
  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      aria-hidden
      className="pointer-events-none absolute inset-0 h-full w-full"
    >
      <defs>
        {annotations.map((annotation, index) => (
          <marker
            key={`head-${index}`}
            id={`arrival-arrow-${index}`}
            viewBox="0 0 10 10"
            refX="8"
            refY="5"
            markerWidth="4"
            markerHeight="4"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill={annotation.color} />
          </marker>
        ))}
      </defs>
      {annotations.map((annotation, index) => (
        <Shape key={index} annotation={annotation} index={index} />
      ))}
    </svg>
  );
}

function Shape({ annotation, index }: { annotation: ArrivalAnnotation; index: number }) {
  const x = annotation.x * 100;
  const y = annotation.y * 100;
  const x2 = annotation.x2 * 100;
  const y2 = annotation.y2 * 100;
  const stroke = annotation.color;

  switch (annotation.kind) {
    case 'arrow':
      return (
        <line
          x1={x}
          y1={y}
          x2={x2}
          y2={y2}
          stroke={stroke}
          strokeWidth={1.4}
          strokeLinecap="round"
          markerEnd={`url(#arrival-arrow-${index})`}
          vectorEffect="non-scaling-stroke"
        />
      );
    case 'circle':
      return (
        <ellipse
          cx={(x + x2) / 2}
          cy={(y + y2) / 2}
          rx={Math.max(Math.abs(x2 - x) / 2, 2)}
          ry={Math.max(Math.abs(y2 - y) / 2, 2)}
          fill="none"
          stroke={stroke}
          strokeWidth={1.4}
          vectorEffect="non-scaling-stroke"
        />
      );
    case 'rect':
      return (
        <rect
          x={Math.min(x, x2)}
          y={Math.min(y, y2)}
          width={Math.max(Math.abs(x2 - x), 2)}
          height={Math.max(Math.abs(y2 - y), 2)}
          fill="none"
          stroke={stroke}
          strokeWidth={1.4}
          vectorEffect="non-scaling-stroke"
        />
      );
    case 'dot':
      return <circle cx={x} cy={y} r={2} fill={stroke} />;
    default:
      // Ein unbekannter Typ aus einer neueren Registry darf das Bild nicht mitreissen.
      return null;
  }
}
